import {useEffect, useMemo, useRef, useState} from 'react';
import {NetworkStatus, useQuery} from '@apollo/client';
import {useSelector} from 'react-redux';
import {Alert, Pagination} from 'antd';
import {AnimatePresence, motion} from 'framer-motion';
import {Outlet, useNavigate, useLocation} from 'react-router-dom';
import {
  AppstoreOutlined,
  UnorderedListOutlined,
  FilterOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import {
  GET_ADMIN_TICKETS,
  GET_ADMIN_TICKETS_MIN,
} from 'graphql/queries/aiHelpdesk';
import {UserSelectField} from 'components/userSelectField/UserSelectField';
import styles from './AiHelpDesk.module.scss';
import {
  FilterSidebar,
  filterSidebarInitialDraft,
} from './components/FilterSidebar';
import {
  EMPTY_SIDEBAR_FILTERS,
  buildAdminTicketsListInput,
  filterTicketsForTab,
  hasActiveSidebarFilters,
  mapHelpdeskTicketToView,
  unwrapAdminTickets,
} from './helpdeskTicketsUtils';
import {useTicketQueueState} from './useTicketQueueState';
import {writeTicketQueueState} from './ticketQueueState';
import {NavTabsEnum} from 'utils/constants';
import {useVisibleSubTabs} from 'utils/useVisibleSubTabs';
import {awaitingCountVariables, readTotal} from './TicketAlertsWatcher';

const HELPDESK_SUBTABS = [
  {key: 'my-tickets', label: 'My Tickets'},
  {key: 'escalated-tickets', label: 'Escalated Tickets'},
  {key: 'ongoing-tickets', label: 'Ongoing Tickets'},
  {key: 'resolved-tickets', label: 'Resolved Tickets'},
  {key: 'abandoned-tickets', label: 'Abandoned Tickets'},
  {key: 'anonymous-tickets', label: 'Anonymous Tickets'},
];

// Not in the role catalog: it's a slice of My Tickets, so it rides on that
// grant and sits right after it.
const AWAITING_TAB = {key: 'awaiting-reply', label: 'Awaiting Your Reply'};

const AiHelpDeskLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const adminId = useSelector(state => state?.auth?.userData?.user?.id);

  const pathTab = location.pathname.split('/').filter(Boolean).pop();
  const activeTab = pathTab === 'ai-helpdesk' ? 'ongoing-tickets' : pathTab;
  const [queueState, updateQueue] = useTicketQueueState();
  const {
    viewMode,
    search: searchInput,
    filters: appliedFilters,
    selectedUsers,
    page: currentPage,
    pageSize,
  } = queueState;
  const {visibleSubTabs: grantedSubTabs, firstVisible} = useVisibleSubTabs(
    NavTabsEnum.CustomerSupport,
    HELPDESK_SUBTABS,
  );
  const visibleSubTabs = grantedSubTabs.flatMap(tab =>
    tab.key === 'my-tickets' ? [tab, AWAITING_TAB] : [tab],
  );
  const visibleKeys = visibleSubTabs.map(tab => tab.key);
  const showAwaiting = visibleKeys.includes(AWAITING_TAB.key);
  const [filterVisible, setFilterVisible] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(searchInput);
  const filtersActive = hasActiveSidebarFilters(appliedFilters);
  const [filterDraft, setFilterDraft] = useState(filterSidebarInitialDraft);
  const [chatTickets, setChatTickets] = useState([]);
  const [chatLoadingMore, setChatLoadingMore] = useState(false);
  const selectedUserIds = useMemo(
    () =>
      (selectedUsers || [])
        .map(user => user?.split?.(';')?.[2])
        .filter(Boolean),
    [selectedUsers],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const media = window.matchMedia('(max-width: 767px)');
    const handleMobileView = event => {
      if (event.matches) {
        if (viewMode === 'chat') updateQueue({viewMode: 'grid', page: 1});
      }
    };

    handleMobileView(media);
    if (media.addEventListener) {
      media.addEventListener('change', handleMobileView);
      return () => media.removeEventListener('change', handleMobileView);
    }

    media.addListener(handleMobileView);
    return () => media.removeListener(handleMobileView);
  }, [viewMode]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (filterVisible) {
      setFilterDraft({
        categoryIds: appliedFilters.categoryIds || [],
        featureIds: appliedFilters.featureIds || [],
        language: appliedFilters.language || 'ALL',
        featureIssueId: appliedFilters.featureIssueId,
        status: appliedFilters.status,
        statuses: appliedFilters.statuses || [],
        unrated: !!appliedFilters.unrated,
        staleAfter: appliedFilters.staleAfter || null,
        aiTurnCountMin: appliedFilters.aiTurnCountMin,
        csatScoreMin: appliedFilters.csatScoreMin,
        csatScoreMax: appliedFilters.csatScoreMax,
      });
    }
  }, [filterVisible, appliedFilters]);

  useEffect(() => {
    if (pathTab === 'ai-helpdesk') {
      navigate(
        {pathname: 'ongoing-tickets', search: location.search},
        {replace: true},
      );
    }
  }, [pathTab, location.search, navigate]);

  // Redirect away from a ticket view the admin can't access.
  useEffect(() => {
    if (firstVisible && !visibleKeys.includes(activeTab)) {
      navigate(
        {pathname: firstVisible.key, search: location.search},
        {replace: true},
      );
    }
  }, [firstVisible, visibleKeys, activeTab, navigate]);

  const listInput = useMemo(
    () =>
      buildAdminTicketsListInput(
        activeTab,
        adminId ? String(adminId) : '',
        debouncedSearch,
        {
          ...appliedFilters,
          userIds: selectedUserIds,
        },
        {
          page: currentPage,
          limit: pageSize,
        },
      ),
    [
      activeTab,
      adminId,
      debouncedSearch,
      appliedFilters,
      selectedUserIds,
      currentPage,
      pageSize,
    ],
  );

  const listSkip = activeTab === 'my-tickets' && !adminId;

  const {
    data: listData,
    loading: listLoading,
    error: listError,
    refetch: refetchTickets,
    networkStatus: listNetworkStatus,
  } = useQuery(GET_ADMIN_TICKETS, {
    variables: {
      input: listInput,
      // Only need the latest event per ticket for the card/table preview.
      eventsInput2: {
        pagination: {page: 1, limit: 1},
        sort: {direction: 'DESC'},
      },
    },
    skip: listSkip,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  });

  // Tab badge. Polls like the app-wide watcher so it stays live on this page.
  const {data: awaitingData, refetch: refetchAwaiting} = useQuery(
    GET_ADMIN_TICKETS_MIN,
    {
      variables: awaitingCountVariables,
      skip: !showAwaiting,
      pollInterval: 22000,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  );
  const awaitingCount = readTotal(awaitingData);
  const awaitingTotal = awaitingCount || 0;

  // The badge polls; when it moves while you're on the Awaiting tab, reload
  // the list too, so a new reply shows up instead of only the count.
  const prevAwaitingCount = useRef(null);
  useEffect(() => {
    const prev = prevAwaitingCount.current;
    prevAwaitingCount.current = awaitingCount;
    if (
      prev !== null &&
      awaitingCount !== null &&
      prev !== awaitingCount &&
      activeTab === AWAITING_TAB.key
    ) {
      refetchTickets();
    }
  }, [awaitingCount, activeTab, refetchTickets]);

  // Background refreshes (a push, a reply, the badge moving) keep the cards on
  // screen instead of flashing skeletons.
  const ticketsLoading =
    listLoading && listNetworkStatus !== NetworkStatus.refetch;

  // Resolving from a card/table action drops a ticket off the tab.
  const refetchTicketsAndCount = () => {
    if (showAwaiting) refetchAwaiting();
    return refetchTickets();
  };

  const listUnwrap = unwrapAdminTickets(listData);
  const listGraphQLError =
    listUnwrap.error?.message ||
    (listError && listError.message) ||
    (listData?.getAdminTickets?.__typename === 'Error'
      ? listData.getAdminTickets.message
      : null);

  const tickets = useMemo(() => {
    if (listSkip) return [];
    let raw = listUnwrap.rows || [];
    raw = filterTicketsForTab(raw, activeTab, appliedFilters);
    return raw.map(t => mapHelpdeskTicketToView(t, adminId));
  }, [listSkip, listUnwrap.rows, activeTab, adminId, appliedFilters]);

  const paginationMeta = listUnwrap.pagination || {};
  const totalTickets = paginationMeta.total || 0;

  const handleTabChange = tab => {
    // Status, CSAT, and "unrated" filters are tab-scoped:
    //   • status — only valid within the new tab's allowed set
    //   • CSAT score / unrated — only meaningful on resolved tickets, since
    //     customers can't rate a ticket they haven't closed yet
    // Drop them on every tab change so a prior tab's pick can't leak.
    const stripScoped = prev => ({
      ...prev,
      statuses: [],
      status: undefined,
      csatScoreMin: undefined,
      csatScoreMax: undefined,
      unrated: false,
    });
    setFilterDraft(stripScoped);
    navigate({
      pathname: tab,
      search: writeTicketQueueState({
        ...queueState,
        page: 1,
        filters: stripScoped(appliedFilters),
      }),
    });
  };

  const handleApplyFilters = () => {
    const nextApplied = {
      categoryIds: filterDraft.categoryIds || [],
      featureIds: filterDraft.featureIds || [],
      language:
        filterDraft.language === 'ALL' ? undefined : filterDraft.language,
      featureIssueId: (filterDraft.featureIssueId || '').trim() || undefined,
      status: filterDraft.status || undefined,
      statuses:
        Array.isArray(filterDraft.statuses) && filterDraft.statuses.length
          ? filterDraft.statuses
          : [],
      unrated: !!filterDraft.unrated,
      staleAfter: filterDraft.staleAfter || null,
      aiTurnCountMin:
        typeof filterDraft.aiTurnCountMin === 'number'
          ? filterDraft.aiTurnCountMin
          : undefined,
      csatScoreMin:
        typeof filterDraft.csatScoreMin === 'number'
          ? filterDraft.csatScoreMin
          : undefined,
      csatScoreMax:
        typeof filterDraft.csatScoreMax === 'number'
          ? filterDraft.csatScoreMax
          : undefined,
    };
    updateQueue({filters: nextApplied, page: 1});
    setFilterVisible(false);
    if (!listSkip) {
      const input = buildAdminTicketsListInput(
        activeTab,
        adminId ? String(adminId) : '',
        debouncedSearch,
        {
          ...nextApplied,
          userIds: selectedUserIds,
        },
        {
          page: 1,
          limit: pageSize,
        },
      );
      refetchTickets({input});
    }
  };

  const handleResetFilters = () => {
    setFilterDraft(filterSidebarInitialDraft);
    updateQueue({selectedUsers: [], filters: EMPTY_SIDEBAR_FILTERS, page: 1});
    setFilterVisible(false);
    if (!listSkip) {
      const input = buildAdminTicketsListInput(
        activeTab,
        adminId ? String(adminId) : '',
        debouncedSearch,
        EMPTY_SIDEBAR_FILTERS,
        {
          page: 1,
          limit: pageSize,
        },
      );
      refetchTickets({input});
    }
  };

  useEffect(() => {
    setChatTickets([]);
    setChatLoadingMore(false);
  }, [
    viewMode,
    activeTab,
    debouncedSearch,
    selectedUserIds,
    appliedFilters,
    pageSize,
  ]);

  useEffect(() => {
    if (viewMode !== 'chat') return;
    if (listLoading) return;
    setChatTickets(prev => {
      if (currentPage === 1) return tickets;
      const seen = new Set(prev.map(t => t.id));
      return [...prev, ...tickets.filter(t => !seen.has(t.id))];
    });
    setChatLoadingMore(false);
  }, [tickets, viewMode, currentPage, listLoading]);

  const chatHasMore =
    viewMode === 'chat' &&
    totalTickets > 0 &&
    chatTickets.length < totalTickets;

  const handleChatLoadMore = () => {
    if (listLoading || chatLoadingMore) return;
    if (!chatHasMore) return;
    setChatLoadingMore(true);
    updateQueue(previous => ({page: previous.page + 1}));
  };

  const handlePaginationChange = (page, size) => {
    updateQueue({page: size !== pageSize ? 1 : page, pageSize: size});
  };

  return (
    <div className={styles.container}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Tickets</h1>
      </header>

      {listGraphQLError ? (
        <Alert type="error" message={listGraphQLError} showIcon />
      ) : null}

      <nav className={styles.routesBar} aria-label="Ticket views">
        <div className={styles.tabs}>
          {visibleSubTabs.map(tab => (
            <span
              key={tab.key}
              className={`${styles.tab} ${
                activeTab === tab.key ? styles.activeTab : ''
              }`}
              onClick={() => handleTabChange(tab.key)}
              role="tab"
              aria-selected={activeTab === tab.key}>
              {tab.label}
              {tab.key === AWAITING_TAB.key && awaitingTotal > 0 ? (
                <span className={styles.tabCount}>{awaitingTotal}</span>
              ) : null}
            </span>
          ))}
        </div>
      </nav>

      <div className={styles.toolbar}>
        <div className={styles.toolbarMain}>
          <input
            type="search"
            placeholder="Search tickets..."
            className={styles.searchBar}
            value={searchInput}
            onChange={e => updateQueue({search: e.target.value, page: 1})}
          />
          <div className={styles.userSelectWrap}>
            <UserSelectField
              users={selectedUsers}
              setUsers={users => updateQueue({selectedUsers: users, page: 1})}
              placeholder="Search user(s)"
            />
          </div>
        </div>
        <div className={styles.toolbarActions}>
          <div className={styles.viewToggleGroup}>
            <button
              type="button"
              aria-label="Grid view"
              className={`${styles.iconBtn} ${
                viewMode === 'grid' ? styles.activeViewMode : ''
              }`}
              onClick={() => updateQueue({viewMode: 'grid', page: 1})}>
              <AppstoreOutlined />
            </button>
            <button
              type="button"
              aria-label="List view"
              className={`${styles.iconBtn} ${
                viewMode === 'list' ? styles.activeViewMode : ''
              }`}
              onClick={() => updateQueue({viewMode: 'list', page: 1})}>
              <UnorderedListOutlined />
            </button>
            <button
              type="button"
              aria-label="Chat view"
              className={`${styles.iconBtn} ${styles.chatModeBtn} ${
                viewMode === 'chat' ? styles.activeViewMode : ''
              }`}
              onClick={() => updateQueue({viewMode: 'chat', page: 1})}>
              <MessageOutlined />
            </button>
          </div>

          <div className={styles.filterDownloadGroup}>
            <button
              type="button"
              className={`${styles.iconBtn} ${styles.filterTrigger} ${
                filtersActive ? styles.activeViewMode : ''
              }`}
              aria-expanded={filterVisible}
              aria-label={
                filtersActive ? 'Filter tickets (active)' : 'Filter tickets'
              }
              onClick={() => setFilterVisible(!filterVisible)}>
              <FilterOutlined /> Filter{filtersActive ? ' •' : ''}
            </button>
            {filtersActive ? (
              <button
                type="button"
                className={styles.iconBtn}
                onClick={handleResetFilters}>
                Clear filters
              </button>
            ) : null}

            <AnimatePresence>
              {filterVisible && (
                <motion.div
                  className={styles.filterModal}
                  initial={{opacity: 0, y: -12}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -12}}
                  transition={{duration: 0.22, ease: 'easeOut'}}
                  onMouseLeave={() => setFilterVisible(false)}>
                  <FilterSidebar
                    draft={filterDraft}
                    onDraftChange={setFilterDraft}
                    onApply={handleApplyFilters}
                    onReset={handleResetFilters}
                    activeTab={activeTab}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <Outlet
          context={{
            viewMode,
            tickets,
            ticketsLoading,
            ticketsError: listGraphQLError,
            refetchTickets: refetchTicketsAndCount,
            listSkip,
            paginationMeta,
            currentPage,
            pageSize,
            totalTickets,
            onPageChange: handlePaginationChange,
            chatTickets,
            chatHasMore,
            chatLoadingMore,
            onChatLoadMore: handleChatLoadMore,
          }}
        />
      </div>
      {viewMode !== 'list' && viewMode !== 'chat' ? (
        <div className={styles.paginationBar}>
          <Pagination
            current={paginationMeta.page || currentPage}
            pageSize={paginationMeta.limit || pageSize}
            total={totalTickets}
            showSizeChanger
            pageSizeOptions={['12', '24', '48']}
            onChange={handlePaginationChange}
            onShowSizeChange={handlePaginationChange}
            showTotal={total => `${total} tickets`}
          />
        </div>
      ) : null}
    </div>
  );
};

export default AiHelpDeskLayout;
